import { getStatus, getStatusForId } from '@ms/yammer-data-support';
import type { EntityStatus } from '@ms/yammer-data-support';
import type {
  AllowedUploadFileType,
  CurrentNetworkUsagePolicyClients_settings_network_usagePolicy as UsagePolicy,
} from '@ms/yammer-graphql';

import type {
  CurrentNetwork,
  CurrentNetworkUsagePolicy,
  CurrentUser,
  Hosts,
  Loki,
  MsGraphConfig,
  Network,
  NetworkPublisherSettings,
  NetworkReportMessageSettings,
  OcvConfig,
  Session,
  SharedConfig,
  SubstrateConfig,
  User,
  YammerState,
} from '../../state/types';
type GetSession = (state: YammerState) => Session;
/**
 * @deprecated This causes performance issues by triggering re-renders whenever the session entity or subentity gets updated.
 * Use selectors (or create new selectors) which return individual primitive values like **getCurrentSessionUserField** instead.
 */
export const getSession: GetSession = (state) => state.session;

const getAdalResources = (state: YammerState) => getAdalSettings(state).resources;
const getAdalSettings = (state: YammerState) => getSession(state).adal;
const getMsalResources = (state: YammerState) => getMsalSettings(state).resources;
const getMsalSettings = (state: YammerState) => getSession(state).msal;
const getCurrentNetwork = (state: YammerState) => getSession(state).network;
const getHosts: (state: YammerState) => Hosts = (state) => getSession(state).hosts;
const getBootstrap = (state: YammerState) => getSession(state).bootstrap;
const getLokiSettings = (state: YammerState) => getSession(state).loki;
const getSharedConfig = (state: YammerState) => getSession(state).shared;

type GetCurrentSessionUser = (state: YammerState) => CurrentUser;
/**
 * @deprecated This causes performance issues by triggering re-renders whenever the entity gets updated.
 * Use **getCurrentSessionUserField** instead.
 */
export const getCurrentSessionUser: GetCurrentSessionUser = (state) => getSession(state).user;

type GetCurrentSessionUserField = <T extends keyof CurrentUser>(
  state: YammerState,
  fieldName: T
) => CurrentUser[T] | undefined;
export const getCurrentSessionUserField: GetCurrentSessionUserField = (state, fieldName) => {
  const user = getCurrentSessionUser(state);

  return user[fieldName];
};

type GetAllowedUploadFileType = (state: YammerState) => AllowedUploadFileType | undefined;
export const getAllowedUploadFileType: GetAllowedUploadFileType = (state) => {
  const currentNetworkSettings = getNetworkPublisherSettings(state);

  return currentNetworkSettings?.allowedUploadFileType;
};

type GetCurrentNetworkId = (state: YammerState) => CurrentNetwork['id'];
export const getCurrentNetworkId: GetCurrentNetworkId = (state) => getCurrentNetwork(state).id;

type GetCurrentNetworkPermalink = (state: YammerState) => CurrentNetwork['permalink'];
export const getCurrentNetworkPermalink: GetCurrentNetworkPermalink = (state) => getCurrentNetwork(state).permalink;

type IsCurrentNetworkNativeModeSharePointOnlyFileStorage = (
  state: YammerState
) => CurrentNetwork['isNativeModeSharePointOnlyFileStorage'];
export const isCurrentNetworkNativeModeSharePointOnlyFileStorage: IsCurrentNetworkNativeModeSharePointOnlyFileStorage =
  (state) => getCurrentNetwork(state).isNativeModeSharePointOnlyFileStorage;

type IsPushNotificationsNetworkSettingEnabled = (state: YammerState) => boolean;
export const isCurrentNetworkPushNotificationsEnabled: IsPushNotificationsNetworkSettingEnabled = (state) =>
  state.session.network.isWebPushNotificationsEnabled;

type IsTranslationEnabled = (state: YammerState) => CurrentNetwork['isTranslationEnabled'];
export const isTranslationEnabled: IsTranslationEnabled = (state) => getCurrentNetwork(state).isTranslationEnabled;

type IsForcedNotificationEnabled = (state: YammerState) => CurrentNetwork['isForcedNotificationEnabled'];
export const isForcedNotificationEnabled: IsForcedNotificationEnabled = (state) =>
  getCurrentNetwork(state).isForcedNotificationEnabled;

type IsDirectMessagesEnabled = (state: YammerState) => CurrentNetwork['isDirectMessagesEnabled'];
export const isDirectMessagesEnabled: IsDirectMessagesEnabled = (state) =>
  getCurrentNetwork(state).isDirectMessagesEnabled && !isCurrentUserGuest(state);

type GetNetworkPublisherSettings = (state: YammerState) => NetworkPublisherSettings['fulfilled'];
export const getNetworkPublisherSettings: GetNetworkPublisherSettings = (state: YammerState) => {
  const session = getSession(state);

  return session?.networkPublisherSettings?.fulfilled;
};

type GetRawUserLocaleSetting = (state: YammerState) => string;
/**
 * Should only use this to initialize the UI language for web; use yammer-i18n-react's
 * getCurrentLocale if you need to know the language/locale to use in UI (eg for UI language, number
 * or time formatting, etc.)
 */
export const getRawUserLocaleSetting: GetRawUserLocaleSetting = (state) => getSession(state).locale;

type CanUploadFilesForDirectMessages = (state: YammerState) => boolean;
export const canUploadFilesForDirectMessages: CanUploadFilesForDirectMessages = (state) => {
  const currentNetworkSettings = getNetworkPublisherSettings(state);

  return !!currentNetworkSettings && currentNetworkSettings.canUploadFilesForDirectMessages;
};

type IsGifShortcutEnabledInNetwork = (state: YammerState) => boolean;
export const isGifShortcutEnabledInNetwork: IsGifShortcutEnabledInNetwork = (state) => {
  const currentNetworkSettings = getNetworkPublisherSettings(state);

  return !!currentNetworkSettings && currentNetworkSettings.isGifShortcutEnabled;
};

type IsAllCompanyGroupMigrated = (state: YammerState) => boolean;
export const isAllCompanyGroupMigrated: IsAllCompanyGroupMigrated = (state) => {
  const publisherSettings = getNetworkPublisherSettings(state);

  return !!publisherSettings && publisherSettings.isAllCompanyGroupMigrated;
};

type GetBootstrapError = (state: YammerState) => Error | null;
export const getBootstrapError: GetBootstrapError = (state) => getBootstrap(state).error;

type IsCurrentSessionUser = (state: YammerState, id: User['id']) => boolean;
export const isCurrentSessionUser: IsCurrentSessionUser = (state, id) => getCurrentSessionUser(state).id === id;

type IsCurrentUserGuest = (state: YammerState) => boolean;
export const isCurrentUserGuest: IsCurrentUserGuest = (state) => getCurrentSessionUser(state).isGuest;

type IsCurrentUserAdmin = (state: YammerState) => boolean;
export const isCurrentUserAdmin: IsCurrentUserAdmin = (state) => {
  const { isNetworkAdmin, isVerifiedNetworkAdmin } = getCurrentSessionUser(state);

  return isNetworkAdmin || isVerifiedNetworkAdmin;
};

type GetAdalResourceForYammer = (state: YammerState) => string;
export const getAdalResourceForYammer: GetAdalResourceForYammer = (state) => getAdalResources(state).yammer;

type GetMsalResourceForYammer = (state: YammerState) => string;
export const getMsalResourceForYammer: GetMsalResourceForYammer = (state) => getMsalResources(state).yammer;

type GetAdalResourceForLoki = (state: YammerState) => string;
export const getAdalResourceForLoki: GetAdalResourceForLoki = (state) => getAdalResources(state).loki;

type GetAdalResourceForGraph = (state: YammerState) => string;
export const getAdalResourceForGraph: GetAdalResourceForGraph = (state) => getAdalResources(state).graph;

type GetAdalResourceForSubstrateNotifications = (state: YammerState) => string;
export const getAdalResourceForSubstrateNotifications: GetAdalResourceForSubstrateNotifications = (state) =>
  getAdalResources(state).substrateNotifications;

type GetAdalResourceForSubstratePushSubscription = (state: YammerState) => string;
export const getAdalResourceForSubstratePushSubscription: GetAdalResourceForSubstratePushSubscription = (state) =>
  getAdalResources(state).substratePushSubscription;

type GetApiHost = (state: YammerState) => string;
export const getApiHost: GetApiHost = (state) => getHosts(state).api;

type GetGraphqlHost = (state: YammerState) => string;
export const getGraphqlHost: GetGraphqlHost = (state) => getHosts(state).graphql;

type GetLocalRealtimeProxyHost = (state: YammerState) => string | undefined;
export const getLocalRealtimeProxyHost: GetLocalRealtimeProxyHost = (state) => getHosts(state).localRealtimeProxy;

type GetShellSuiteHeaderHost = (state: YammerState) => string;
export const getShellSuiteHeaderHost: GetShellSuiteHeaderHost = (state) => getHosts(state).shellSuiteHeader;

type GetLegacyHost = (state: YammerState) => string;
export const getLegacyHost: GetLegacyHost = (state) => getHosts(state).legacy;

type GetWebHost = (state: YammerState) => string;
export const getWebHost: GetWebHost = (state) => getHosts(state).web;

type GetBroadcastHost = (state: YammerState) => string;
export const getBroadcastHost: GetBroadcastHost = (state) => getHosts(state).broadcast;

type GetTenorHost = (state: YammerState) => string;
export const getTenorHost: GetTenorHost = (state) => getHosts(state).tenor;

type GetThumbnailsHost = (state: YammerState) => string;
export const getThumbnailsHost: GetThumbnailsHost = (state) => getHosts(state).thumbnails;

type GetThumbnailsEngageHost = (state: YammerState) => string;
export const getThumbnailsEngageHost: GetThumbnailsEngageHost = (state) => getHosts(state).thumbnailsEngage;

type GetTranslateHost = (state: YammerState) => string;
export const getTranslateHost: GetTranslateHost = (state) => getHosts(state).translate;

type GetMsGraphHost = (state: YammerState) => string;
export const getMsGraphHost: GetMsGraphHost = (state) => getHosts(state).msGraph;

type GetEcsHost = (state: YammerState) => string;
export const getEcsHost: GetEcsHost = (state) => getHosts(state).ecs;

type GetOcvHost = (state: YammerState) => string;
export const getOcvHost: GetOcvHost = (state) => getHosts(state).ocv;

type GetSubstrateHost = (state: YammerState) => string;
export const getSubstrateHost: GetSubstrateHost = (state) => getHosts(state).substrate;

type GetOcvConfig = (state: YammerState) => OcvConfig;
export const getOcvConfig: GetOcvConfig = (state) => getSession(state).ocv;

type GetSubstrateConfig = (state: YammerState) => SubstrateConfig;
export const getSubstrateConfig: GetSubstrateConfig = (state) => getSession(state).substrate;

type GetMsGraphConfig = (state: YammerState) => MsGraphConfig;
export const getMsGraphConfig: GetMsGraphConfig = (state) => getSession(state).msGraph;

type GetCurrentNetworkUsagePolicy = (state: YammerState) => CurrentNetworkUsagePolicy['fulfilled'];
export const getCurrentNetworkUsagePolicy: GetCurrentNetworkUsagePolicy = (state) =>
  getSession(state).networkUsagePolicy.fulfilled;

type IsCurrentNetworkUsagePolicyDeclined = (state: YammerState) => CurrentNetworkUsagePolicy['isDeclined'];
export const isCurrentNetworkUsagePolicyDeclined: IsCurrentNetworkUsagePolicyDeclined = (state) =>
  getSession(state).networkUsagePolicy.isDeclined;

type GetCurrentNetworkUsagePolicyThrows = (state: YammerState) => UsagePolicy;
export const getCurrentNetworkUsagePolicyThrows: GetCurrentNetworkUsagePolicyThrows = (state) => {
  const networkUsagePolicy = getCurrentNetworkUsagePolicy(state);

  if (!networkUsagePolicy) {
    throw new Error('Can not find network usage policy');
  }

  return networkUsagePolicy;
};

type ShouldFetchNetworkUsagePolicy = (state: YammerState) => boolean;
export const shouldFetchNetworkUsagePolicy: ShouldFetchNetworkUsagePolicy = (state) =>
  getCurrentNetworkUsagePolicyStatus(state) === 'notRequested';

type GetCurrentNetworkUsagePolicyStatus = (state: YammerState) => EntityStatus;
export const getCurrentNetworkUsagePolicyStatus: GetCurrentNetworkUsagePolicyStatus = (state) => {
  const session = getSession(state);

  if (session && session.networkUsagePolicy) {
    const networkUsagePolicy = session.networkUsagePolicy;

    if (networkUsagePolicy.pending) {
      return 'pending';
    }

    if (networkUsagePolicy.fulfilled) {
      return 'fulfilled';
    }

    if (networkUsagePolicy.rejected) {
      return 'rejected';
    }
  }

  return 'notRequested';
};

type NoAccessUser = (state: YammerState) => boolean;
export const noAccessUser: NoAccessUser = (state) => {
  const error = getBootstrapError(state);

  return error ? error.message === 'noAccessForResource' : false;
};

type GetLokiClientType = (state: YammerState) => Loki['clientType'];
export const getLokiClientType: GetLokiClientType = (state) => getLokiSettings(state).clientType;

type GetLokiRegion = (state: YammerState) => Loki['region'];
export const getLokiRegion: GetLokiRegion = (state) => getLokiSettings(state).region;

type GetLokiComplianceEnvironment = (state: YammerState) => Loki['complianceEnvironment'];
export const getLokiComplianceEnvironment: GetLokiComplianceEnvironment = (state) =>
  getLokiSettings(state).complianceEnvironment;

type ShouldFetchPublisherAttachmentSettings = (state: YammerState) => boolean;
export const shouldFetchPublisherAttachmentSettings: ShouldFetchPublisherAttachmentSettings = (state) =>
  getNetworkPublisherSettingsStatus(state) === 'notRequested';

type GetNetworkPublisherSettingsStatus = (state: YammerState) => EntityStatus;
export const getNetworkPublisherSettingsStatus: GetNetworkPublisherSettingsStatus = (state) =>
  getStatus(getSession(state).networkPublisherSettings);

type ShouldFetchHideUserSettings = (state: YammerState) => boolean;
export const shouldFetchHideUserSettings: ShouldFetchHideUserSettings = (state) =>
  getNetworkHideUserSettingsStatus(state) === 'notRequested';

type GetNetworkHideUserSettingsStatus = (state: YammerState) => EntityStatus;
export const getNetworkHideUserSettingsStatus: GetNetworkHideUserSettingsStatus = (state) =>
  getStatus(getSession(state).networkHideUserSettings);

type IsHideUserEnabledForNetwork = (state: YammerState) => boolean;
export const isHideUserEnabledForNetwork: IsHideUserEnabledForNetwork = (state) => {
  const session = getSession(state);
  const networkHideUserSettingsFullfilled = session.networkHideUserSettings?.fulfilled;

  return !!networkHideUserSettingsFullfilled && networkHideUserSettingsFullfilled.isHideUsersForViewerEnabled;
};

type GetNetworkReportMessageSettingsStatus = (state: YammerState, networkId: Network['id']) => EntityStatus;
export const getNetworkReportMessageSettingsStatus: GetNetworkReportMessageSettingsStatus = (state, networkId) =>
  getStatusForId(getSession(state).networkReportMessageSettings, networkId);

type ShouldFetchReportMessageSettingsForNetwork = (state: YammerState, networkId: Network['id']) => boolean;
export const shouldFetchReportMessageSettingsForNetwork: ShouldFetchReportMessageSettingsForNetwork = (
  state,
  networkId
) => getNetworkReportMessageSettingsStatus(state, networkId) === 'notRequested';

type IsReportMessageEnabledForNetwork = (state: YammerState, networkId: Network['id']) => boolean;
export const isReportMessageEnabledForNetwork: IsReportMessageEnabledForNetwork = (state, networkId) => {
  const settings = getNetworkReportMessageSettings(state, networkId);

  return !!settings && settings.isEnabled;
};

type GetNetworkReportMessageSettings = (
  state: YammerState,
  networkId: Network['id']
) => NetworkReportMessageSettings | undefined;
export const getNetworkReportMessageSettings: GetNetworkReportMessageSettings = (state, networkId) =>
  getSession(state)?.networkReportMessageSettings?.fulfilled.byId[networkId];

type CanCurrentUserSendOnBehalfOf = (state: YammerState) => boolean;
export const canCurrentUserSendOnBehalfOf: CanCurrentUserSendOnBehalfOf = (state: YammerState) =>
  getCurrentSessionUser(state).canSendOnBehalfOf;

type GetSharedSettings = (state: YammerState) => SharedConfig;
export const getSharedSettings: GetSharedSettings = (state) => getSharedConfig(state);

type GetDesignerAppHost = (state: YammerState) => string;
export const getDesignerAppHost: GetDesignerAppHost = (state) => getHosts(state).designerApp;

type GetDesignerMiniAppHost = (state: YammerState) => string;
export const getDesignerMiniAppHost: GetDesignerMiniAppHost = (state) => getHosts(state).designerMiniApp;

type GetDesignerAppServiceHost = (state: YammerState) => string;
export const getDesignerAppServiceHost: GetDesignerAppServiceHost = (state) => getHosts(state).designerAppService;

type IsCurrentUserRestricted = (state: YammerState) => boolean;
export const isCurrentUserRestricted: IsCurrentUserRestricted = (state) =>
  getCurrentSessionUser(state).isRestrictedToViewOnlyMode;
